/**
 * <ugc-stories> – rad med kundvideor (9:16, spelas tyst i raden) eller Instagram-cirklar
 * på produktsidan. Klick öppnar videon med ljud (eller bilden) i en <dialog>.
 * Videon ligger i en <template> per kund och laddas först när rutan öppnas.
 * Paus-knappen (data-ugc-toggle) stoppar/startar de tysta videorna i raden;
 * vid "minska rörelse" börjar de pausade.
 */
class UgcStories extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {HTMLDialogElement | null} */
  #dialog = null;
  /** @type {HTMLElement | null} */
  #player = null;
  /** @type {HTMLElement | null} */
  #handleEl = null;
  /** @type {HTMLAnchorElement | null} */
  #linkEl = null;
  /** @type {HTMLButtonElement | null} */
  #prev = null;
  /** @type {HTMLButtonElement | null} */
  #next = null;
  /** @type {HTMLButtonElement | null} */
  #toggle = null;
  /** @type {HTMLElement[]} */
  #triggers = [];
  /** @type {HTMLVideoElement[]} */
  #inlineVideos = [];
  #currentIndex = 0;
  #paused = false;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#dialog = this.querySelector('[data-ugc-dialog]');
    this.#player = this.querySelector('[data-ugc-player]');
    this.#handleEl = this.querySelector('[data-ugc-dialog-handle]');
    this.#linkEl = this.querySelector('[data-ugc-dialog-link]');
    this.#prev = this.querySelector('[data-ugc-prev]');
    this.#next = this.querySelector('[data-ugc-next]');
    this.#toggle = this.querySelector('[data-ugc-toggle]');
    this.#triggers = Array.from(this.querySelectorAll('[data-ugc-open]'));
    this.#inlineVideos = Array.from(this.querySelectorAll('[data-ugc-inline-video]'));
    this.#currentIndex = 0;
    this.#setupInlineVideos(signal);

    for (const [index, trigger] of this.#triggers.entries()) {
      trigger.addEventListener('click', () => this.open(index), { signal });
    }

    this.querySelector('[data-ugc-close]')?.addEventListener('click', () => this.close(), { signal });
    this.#prev?.addEventListener('click', () => this.#step(-1), { signal });
    this.#next?.addEventListener('click', () => this.#step(1), { signal });

    if (!this.#dialog) return;
    this.#dialog.addEventListener(
      'click',
      (event) => {
        if (event.target === this.#dialog) this.close();
      },
      { signal }
    );
    this.#dialog.addEventListener('keydown', (event) => this.#onDialogKeydown(event), { signal });
    this.#dialog.addEventListener('close', () => this.#onClosed(), { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#observer?.disconnect();
    this.#observer = null;
    document.body.classList.remove('ugc-dialog-open');
  }

  /**
   * Öppnar rutan med kund nummer `index` (bland knapparna som öppnar rutan).
   * @param {number} index
   */
  open(index) {
    const trigger = this.#triggers[index];
    if (!trigger || !this.#dialog || !this.#player) return;
    this.#pauseInline();

    this.#currentIndex = index;

    const template = trigger.closest('[data-ugc-item]')?.querySelector('template[data-ugc-media]');
    this.#player.replaceChildren(template ? template.content.cloneNode(true) : document.createDocumentFragment());

    const video = this.#player.querySelector('video');
    if (video) {
      video.setAttribute('playsinline', '');
      video.controls = true;
      video.loop = true;
      video.muted = false;
      video.play()?.catch(() => {
        // Ljud utan tillåtelse → spela tyst så att något ändå händer.
        video.muted = true;
        video.play()?.catch(() => {});
      });
    }

    if (this.#handleEl) this.#handleEl.textContent = trigger.dataset.ugcHandle || '';
    if (this.#linkEl) {
      const href = trigger.dataset.ugcLink;
      if (href) {
        this.#linkEl.href = href;
        this.#linkEl.hidden = false;
      } else {
        this.#linkEl.removeAttribute('href');
        this.#linkEl.hidden = true;
      }
    }

    const multiple = this.#triggers.length > 1;
    if (this.#prev) this.#prev.hidden = !multiple;
    if (this.#next) this.#next.hidden = !multiple;

    if (!this.#dialog.open) {
      if (typeof this.#dialog.showModal === 'function') {
        this.#dialog.showModal();
      } else {
        this.#dialog.setAttribute('open', '');
      }
    }
    document.body.classList.add('ugc-dialog-open');
  }

  close() {
    if (!this.#dialog) return;
    if (typeof this.#dialog.close === 'function') {
      this.#dialog.close(); // 'close'-händelsen kör #onClosed()
    } else {
      this.#dialog.removeAttribute('open');
      this.#onClosed();
    }
  }

  /** @param {KeyboardEvent} event */
  #onDialogKeydown(event) {
    if (event.key === 'Escape') {
      // Fallback om temats globala Escape-hantering stoppar dialogens inbyggda stängning.
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    // Piltangenter i videospelaren (spola, volym) ska inte byta kund.
    if (event.target instanceof Element && event.target.closest('video')) return;
    this.#step(event.key === 'ArrowRight' ? 1 : -1);
  }

  /** @param {number} direction - 1 = nästa, -1 = föregående */
  #step(direction) {
    const total = this.#triggers.length;
    if (total < 2) return;
    this.open((this.#currentIndex + direction + total) % total);
  }

  #onClosed() {
    this.#teardown();
    // Fokus tillbaka till kunden som visades sist (efter Föregående/Nästa), inte bara den som klickades.
    this.#triggers[this.#currentIndex]?.focus();
  }

  #teardown() {
    this.#player?.querySelector('video')?.pause();
    this.#player?.replaceChildren();
    document.body.classList.remove('ugc-dialog-open');
    this.#resumeInline();
  }

  /**
   * Videorna i raden spelas tyst när de syns (och laddas först då) och pausas utanför bild.
   * Paus-knappen visas först här, när skriptet faktiskt styr uppspelningen.
   * @param {AbortSignal} signal
   */
  #setupInlineVideos(signal) {
    if (!this.#inlineVideos.length || !('IntersectionObserver' in window)) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#paused = reduceMotion.matches;
    reduceMotion.addEventListener('change', (event) => this.#setPaused(event.matches), { signal });

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const video = /** @type {HTMLVideoElement} */ (entry.target);
          video.dataset.ugcVisible = entry.isIntersecting ? '1' : '';
          if (entry.isIntersecting) {
            this.#playInline(video, signal);
          } else {
            video.pause();
          }
        }
      },
      { threshold: 0.35 }
    );
    for (const video of this.#inlineVideos) this.#observer.observe(video);

    if (this.#toggle) {
      this.#toggle.addEventListener('click', () => this.#setPaused(!this.#paused), { signal });
      this.#syncToggle();
      this.#toggle.hidden = false;
      const header = this.#toggle.closest('.ugc-stories__header');
      if (header instanceof HTMLElement) header.hidden = false;
    }
  }

  /** @param {boolean} paused */
  #setPaused(paused) {
    this.#paused = paused;
    this.#syncToggle();
    if (paused) {
      this.#pauseInline();
    } else {
      this.#resumeInline();
    }
  }

  #syncToggle() {
    this.#toggle?.setAttribute('aria-pressed', String(this.#paused));
  }

  /**
   * @param {HTMLVideoElement} video
   * @param {AbortSignal} [signal]
   */
  #playInline(video, signal) {
    if (this.#paused || this.#dialog?.open) return;
    video.muted = true;
    if (!video.dataset.ugcLoaded) {
      video.dataset.ugcLoaded = '1';
      video.preload = 'auto';
      // play() startar själv nedladdningen; om den avbryts under laddningen försöker vi igen när videon kan spelas.
      video.addEventListener(
        'canplay',
        () => {
          if (video.paused && video.dataset.ugcVisible === '1' && !this.#paused && !this.#dialog?.open) {
            video.play()?.catch(() => {});
          }
        },
        { once: true, signal }
      );
    }
    video.play()?.catch(() => {});
  }

  #pauseInline() {
    for (const video of this.#inlineVideos) video.pause();
  }

  #resumeInline() {
    if (this.#paused) return;
    for (const video of this.#inlineVideos) {
      if (video.dataset.ugcVisible === '1') this.#playInline(video, this.#abort?.signal);
    }
  }
}

if (!customElements.get('ugc-stories')) {
  customElements.define('ugc-stories', UgcStories);
}
